import { useAmplitude } from '@/context/me/amplitude.context';
import { useAnalytics } from '@cmpsr/analytics';

// Dashboard source tracking for Page Viewed and Modal Viewed events
export enum DashboardSource {
  DASHBOARD_SPECIFIC_CANCER = 'dashboard_specific_cancer',
  DASHBOARD_CANCER_CTA = 'dashboard_cancer_CTA',
  DASHBOARD_SPECIFIC_RISK_FACTOR = 'dashboard_specific_risk_factor',
  DASHBOARD_RISK_FACTOR_CTA = 'dashboard_risk_factor_CTA',
  DASHBOARD_SPECIFIC_TEST = 'dashboard_specific_test',
  DASHBOARD_TEST_CTA = 'dashboard_test_CTA',
  DASHBOARD_SPECIFIC_ACTION = 'dashboard_specific_action',
  DASHBOARD_ACTION_CTA = 'dashboard_action_CTA',
  DASHBOARD_HEALTH_DATA_CTA = 'dashboard_health_data_CTA',
  DASHBOARD_LABS_CTA = 'dashboard_labs_CTA',
  DASHBOARD_LABS_HERO = 'dashboard_labs_hero',
  RISK_SCORE_REVEAL = 'risk_score_reveal',
}

// MODULE-LEVEL source tracking (shared across all hook instances)
let currentNavigationSource: DashboardSource | null = null;

// MODULE-LEVEL additional params tracking (shared across all hook instances)
let sharedAdditionalParams: Record<string, unknown> | null = null;

// This hook centralizes analytics calls shaping and defining them according
// to their definition by the product team found here: https://docs.google.com/spreadsheets/d/1YMh5n5VeEizcfkqgoH3qgPrxcRnSTFvHd96yCD23Dj8/
export default function useAnalyticsCalls() {
  const analytics = useAnalytics();
  const amplitude = useAmplitude();

  const analyticsWrapper = {
    track: (eventName: string, traits: Record<string, string | number> = {}) => {
      analytics.track(eventName, traits);
      amplitude.track(eventName, traits);
    },
    identify: (userId: string, traits: Record<string, string | number> = {}) => {
      amplitude.setUserId(userId);
      if (traits) {
        const identifyEvent = new amplitude.Identify();
        Object.entries(traits).forEach(([key, value]) => {
          identifyEvent.set(key, value);
        });
        amplitude.identify(identifyEvent);
      }
    },
  };

  const setNavigationSource = (source: DashboardSource) => {
    currentNavigationSource = source;
  };

  const setAnalyticsParams = (additionalParams: Record<string, unknown>) => {
    sharedAdditionalParams = { ...additionalParams };
  };

  const callPageView = (additionalParams: Record<string, unknown> = {}) => {
    // Consume source (used once then cleared)
    const source = currentNavigationSource;
    currentNavigationSource = null;

    // Combine module-level additional params with provided params, then clear
    const combinedParams = {
      ...sharedAdditionalParams,
      ...additionalParams,
    };
    sharedAdditionalParams = null;


    analyticsWrapper.track('[Amplitude] Page Viewed', {
      '[Amplitude] Page Domain': window.location.hostname,
      '[Amplitude] Page Location': window.location.href,
      '[Amplitude] Page Path': window.location.pathname,
      '[Amplitude] Page Title': window.document.title,
      '[Amplitude] Page URL': window.location.href,
      // Use provided referrer from additionalParams (for SPA navigation) or fall back to document.referrer (for external navigation)
      referrer: (additionalParams.referrer as string) || document.referrer,
      page_name: window.location.pathname,
      ...(source && { source }), // Only include source if it exists
      ...combinedParams,
    });
  };

  const callIdentifyEmail = (id: string, email: string) => analyticsWrapper.identify(id, { email });

  const callIdentifyMembership = (id: string, email: string, userType: string) =>
    analyticsWrapper.identify(id, { email, user_type: userType, membership_start_date: new Date().toISOString() });

  const callIdentifySignUp = (id: string, email: string, userType: string) =>
    analyticsWrapper.identify(id, { email, user_type: userType, account_creation_date: new Date().toISOString() });

  const callSignInStarted = () =>
    analyticsWrapper.track('Sign In - Started', {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });

  const callSignInCompleted = () =>
    analyticsWrapper.track('Sign In - Completed', {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });

  const callLogoutCompleted = (type = 'manual') =>
    analyticsWrapper.track('Log Out - Completed', {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
      logout_method: type,
    });

  const callOnboardingStarted = () => analyticsWrapper.track('Onboarding - Started');

  const callOnboardingCompleted = () => analyticsWrapper.track('Onboarding - Completed');

  const callOnboardingSectionCompleted = (onboardingSection: string) =>
    analyticsWrapper.track('Onboarding - Section Completed', {
      onboarding_section: onboardingSection,
    });

  const callOnboardingViewScreen = (screenName: string, section: string, questionId: string) =>
    analyticsWrapper.track('Onboarding Screen - Viewed', {
      screen_name: screenName,
      onboarding_section: section,
      question_number: questionId,
    });

  const callPaymentScreenViewed = (baseAmount: number, discountAmount: number, finalAmount: number) =>
    analyticsWrapper.track('Payment - Screen Viewed', {
      list_price_displayed: baseAmount,
      discount_displayed: discountAmount,
      total_price_displayed: finalAmount,
    });

  const callRevenue = (revenue: number, type = 'new') => analytics.revenue(revenue, 1, type);

  const callMembershipCancel = () => analyticsWrapper.track('Membership - Initiate Cancel');

  const callMembershipRenew = () => analyticsWrapper.track('Membership - Initiate Renewal');

  const callActionPlanItemCompleted = (itemId: string, itemDescription: string) =>
    analyticsWrapper.track('Action Plan - Mark Item Completed', {
      action_plan_item_id: itemId,
      action_plan_item_description: itemDescription,
    });

  const callRiskFactorViewed = (riskFactor: string, riskFactorSlug: string) =>
    callModalViewed('Risk Factor', {
      risk_factor: riskFactor,
      risk_factor_slug: riskFactorSlug,
    });

  const callPpraModalViewed = (surveyItemName: string) =>
    callModalViewed('PPRA', {
      survey_item_name: surveyItemName,
    });

  const callPpraFakePaymentSubmitted = (amount: string) =>
    analyticsWrapper.track('Sign Up - Payment Submitted', {
      form_id: 'ppra-payment-form',
      form_name: 'PPRA Payment Form',
      account_type: 'user',
      payment_method: 'unknown',
      payment_status: 'incomplete',
      promo_code: `PPRA${amount}`,
      coupon_id: `PPRA${amount}`,
      subscription_id: `PPRA${amount}`,
      payment_amount: amount,
    });

  const callModalViewed = (group: string, additionalParams: Record<string, string> = {}) => {
    // Consume source (used once then cleared)
    const source = currentNavigationSource;
    currentNavigationSource = null;

    analyticsWrapper.track(`Modal Viewed`, {
      modal_group: group,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      ...(source && { source }), // Only include source if it exists
      ...additionalParams,
    });
  };

  const callTestResultsModalViewed = (testTitle: string, testSlug: string) =>
    callModalViewed('Test Results', {
      test_title: testTitle,
      test_slug: testSlug,
    });

  const callActionPlanInterventionViewed = (interventionTitle: string, interventionSlug: string) =>
    callModalViewed('Action Plan Intervention', {
      intervention_title: interventionTitle,
      intervention_slug: interventionSlug,
    });

  const callAnswerSubmitted = (
    sectionName: string,
    screenName: string,
    answerValue: string | number,
    questionnaireType: string,
    questionId: string,
    questionType: string,
  ) => {
    analyticsWrapper.track('Answer Submitted', {
      section_name: sectionName,
      screen_name: screenName,
      answer_value: answerValue,
      questionnaire_type: questionnaireType,
      question_id: questionId,
      question_type: questionType,
    });
  };

  const callLinkClicked = (linkText: string, linkUrl: string) => {
    const linkPath = linkUrl ? new URL(linkUrl).pathname : '';
    analyticsWrapper.track('Link Clicked', {
      link_text: linkText,
      link_url: linkUrl,
      link_path: linkPath,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callToggleRisk = (previousRiskType: string, newRiskType: string) => {
    analyticsWrapper.track('Toggle Risk', {
      previous_risk_type: previousRiskType,
      new_risk_type: newRiskType,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callDashboardTabChanged = (previousTab: string, newTab: string) => {
    analyticsWrapper.track('Dashboard Tab Changed', {
      previous_tab: previousTab,
      new_tab: newTab,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callHealthDataCategoryToggled = (categoryName: string, isEditing: boolean) => {
    analyticsWrapper.track('Health Data Toggle', {
      category_toggled: categoryName.toLowerCase(),
      is_editing: isEditing ? 'yes' : 'no',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callDownloadLabs = (type: string) => {
    analyticsWrapper.track('Download Labs', {
      type: type,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callProductPaywall = (paywallType: 'Subscribe' | 'Fix Payment' | 'Fix Payment + Cancel') => {
    analyticsWrapper.track('Product Paywall', {
      paywall_type: paywallType,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callProductPaymentPage = (source: 'paywall' | 'account') => {
    analyticsWrapper.track('Product Payment Page', {
      source: source,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  return {
    callIdentifyEmail,
    callIdentifyMembership,
    callIdentifySignUp,
    callSignInStarted,
    callSignInCompleted,
    callLogoutCompleted,
    callOnboardingStarted,
    callOnboardingCompleted,
    callOnboardingSectionCompleted,
    callOnboardingViewScreen,
    callRevenue,
    callMembershipCancel,
    callMembershipRenew,
    callActionPlanItemCompleted,
    callPaymentScreenViewed,
    callRiskFactorViewed,
    callPpraModalViewed,
    callPpraFakePaymentSubmitted,
    callTestResultsModalViewed,
    callActionPlanInterventionViewed,
    callModalViewed,
    callPageView,
    callAnswerSubmitted,
    callLinkClicked,
    callToggleRisk,
    callDashboardTabChanged,
    callHealthDataCategoryToggled,
    callDownloadLabs,
    callProductPaywall,
    callProductPaymentPage,
    setNavigationSource,
    setAnalyticsParams,
  };
}
